import { Spin, Empty, Typography, Button, Modal, Form, Input, InputNumber, Select, Table, Checkbox } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAxiosPost from '../../hooks/useAxiosPost';
import useMultiNotification from '../../hooks/useNotification';
import { getUserType } from '../../helpers/userDetails';
import { formatDateOnly } from '../../helpers/date';
import './packagesTable.css';

const { Option } = Select;

interface PackageTableDataType {
  key: number;
  intPkPackageID: number;
  intPkCompanyID: number;
  packageName: string;
  speedUp: number;
  speedDown: number;
  speedUnit: 'K' | 'M' | 'G';
  packageDurationEnum: 'HOURS' | 'DAYS';
  packageDurationDays: number;
  packageDurationHours: number;
  packageCreatedDate?: string;
  maxDataVolumeLimit?: number | null;
  maxDataVolumeUnit?: 'K' | 'M' | 'G' | null;
}

const PackagesComponent = () => {
  const { openNotification, contextHolder } = useMultiNotification();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [packageDurationEnum, setPackageDurationEnum] = useState<'HOURS' | 'DAYS' | undefined>(undefined);
  const [maxDataVolumeProvided, setMaxDataVolumeProvided] = useState(false);

  const [postApiData, apiResponse, apiError, apiLoading, apiMessage, apiErrorCount] =
    useAxiosPost<ResponseProp>('packages/all');

  const [
    createPackageData,
    createPackageResponse,
    createPackageError,
    createPackageLoading,
    createPackageMessage,
    createPackageErrorCount,
  ] = useAxiosPost<ResponseProp>('packages/create');

  const [allPackages, setAllPackages] = useState<PackageTableDataType[]>([]);
  const postApiDataRef = useRef(postApiData);
  const hasProcessedCreateSuccess = useRef(false);
  const lastProcessedErrorCount = useRef({ api: 0, create: 0 });

  // Keep ref updated
  useEffect(() => {
    postApiDataRef.current = postApiData;
  }, [postApiData]);

  useEffect(() => {
    postApiDataRef.current({});
  }, []);

  useEffect(() => {
    if (apiResponse !== null) {
      const responseData: any = apiResponse.responseData;
      if (Array.isArray(responseData)) {
        // Transform data for table with keys
        const packagesWithKeys = responseData.map((pkg: any) => ({
          ...pkg,
          key: pkg.intPkPackageID,
        }));
        setAllPackages(packagesWithKeys);
      } else {
        setAllPackages([]);
      }
    }
  }, [apiResponse]);

  useEffect(() => {
    if (apiError !== null && apiErrorCount !== 0 && lastProcessedErrorCount.current.api !== apiErrorCount) {
      lastProcessedErrorCount.current.api = apiErrorCount;
      openNotification('error', 'Failed to Load Packages', apiError);
    }
  }, [apiError, apiErrorCount, openNotification]);

  // Handle create package success - only process once
  useEffect(() => {
    if (createPackageResponse !== null && !hasProcessedCreateSuccess.current) {
      hasProcessedCreateSuccess.current = true;
      openNotification('success', 'Package Created', 'Package created successfully');
      setIsModalOpen(false);
      form.resetFields();
      setPackageDurationEnum(undefined);
      setMaxDataVolumeProvided(false);
      // Refresh packages list
      setTimeout(() => {
        postApiDataRef.current({});
      }, 100);
    }
  }, [createPackageResponse]);

  // Reset the flag when modal opens or createPackageResponse becomes null
  useEffect(() => {
    if (createPackageResponse === null) {
      hasProcessedCreateSuccess.current = false;
    }
  }, [createPackageResponse]);

  // Handle create package error
  useEffect(() => {
    if (
      createPackageError !== null &&
      createPackageErrorCount !== 0 &&
      lastProcessedErrorCount.current.create !== createPackageErrorCount
    ) {
      lastProcessedErrorCount.current.create = createPackageErrorCount;
      openNotification('error', 'Failed to Create Package', createPackageError);
    }
  }, [createPackageError, createPackageErrorCount, openNotification]);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    if (!createPackageLoading) {
      setIsModalOpen(false);
      form.resetFields();
      setPackageDurationEnum(undefined);
      setMaxDataVolumeProvided(false);
    }
  };

  const handleDataLimitCheckboxChange = (e: { target: { checked: boolean } }) => {
    const checked = e.target.checked;
    setMaxDataVolumeProvided(checked);
    if (!checked) {
      form.setFieldsValue({
        maxDataVolumeLimit: undefined,
        maxDataVolumeUnit: undefined,
      });
    }
  };

  const handleCreate = (values: any) => {
    const {
      packageName,
      speedUp,
      speedDown,
      speedUnit,
      packageDurationEnum: durationEnum,
      packageDurationDays,
      packageDurationHours,
      maxDataVolumeLimit,
      maxDataVolumeUnit,
    } = values;

    createPackageData({
      packageName: packageName.trim(),
      speedUp,
      speedDown,
      speedUnit,
      packageDurationEnum: durationEnum,
      packageDurationDays: durationEnum === 'DAYS' ? packageDurationDays : 0,
      packageDurationHours: durationEnum === 'HOURS' ? packageDurationHours : 0,
      maxDataVolumeLimit: maxDataVolumeProvided && maxDataVolumeLimit ? maxDataVolumeLimit : null,
      maxDataVolumeUnit: maxDataVolumeProvided && maxDataVolumeUnit ? maxDataVolumeUnit : null,
    });
  };

  const handleModalClose = () => {
    if (!createPackageLoading) {
      handleCancel();
    }
  };

  const handleRowClick = (record: PackageTableDataType) => {
    navigate(`/package-details/${record.intPkPackageID}`);
  };

  const getSpeedDisplay = (speed: number, unit: string) => {
    return `${speed} ${unit}bps`;
  };

  const getDurationDisplay = (pkg: PackageTableDataType) => {
    if (pkg.packageDurationEnum === 'HOURS') {
      return `${pkg.packageDurationHours} Hour${pkg.packageDurationHours !== 1 ? 's' : ''}`;
    } else {
      return `${pkg.packageDurationDays} Day${pkg.packageDurationDays !== 1 ? 's' : ''}`;
    }
  };

  const getDataVolumeDisplay = (pkg: PackageTableDataType) => {
    if (pkg.maxDataVolumeLimit != null && pkg.maxDataVolumeUnit) {
      return `${pkg.maxDataVolumeLimit} ${pkg.maxDataVolumeUnit}B`;
    }
    return 'No limit';
  };

  const columns: ColumnsType<PackageTableDataType> = [
    {
      title: 'Package Name',
      dataIndex: 'packageName',
      key: 'packageName',
      render: (text: string) => <Typography.Text strong>{text}</Typography.Text>,
    },
    {
      title: 'Download Speed',
      key: 'downloadSpeed',
      render: (_, record) => getSpeedDisplay(record.speedDown, record.speedUnit),
    },
    {
      title: 'Upload Speed',
      key: 'uploadSpeed',
      render: (_, record) => getSpeedDisplay(record.speedUp, record.speedUnit),
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_, record) => getDurationDisplay(record),
    },
    {
      title: 'Data Limit',
      key: 'dataVolume',
      render: (_, record) => getDataVolumeDisplay(record),
    },
    {
      title: 'Date Created',
      dataIndex: 'packageCreatedDate',
      key: 'packageCreatedDate',
      render: (date: string) => {
        if (!date) return '-';
        return formatDateOnly(date);
      },
    },
  ];

  const userRole = getUserType();
  const canCreatePackage = userRole === 'ADMIN';

  return (
    <>
      {contextHolder}
      {canCreatePackage && (
        <div
          style={{
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button type="primary" icon={<PlusOutlined />} onClick={showModal} size="large">
            Create Package
          </Button>
        </div>
      )}

      <Modal
        title="Create New Package"
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        maskClosable={!createPackageLoading}
        closable={!createPackageLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate} requiredMark={false}>
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Package Name</span>}
            name="packageName"
            rules={[
              {
                required: true,
                message: 'Please enter package name',
              },
              {
                min: 1,
                message: 'Package name cannot be empty',
              },
              {
                max: 45,
                message: 'Package name must be less than 45 characters',
              },
              {
                whitespace: true,
                message: 'Package name cannot be only spaces',
              },
            ]}
          >
            <Input placeholder="Enter package name" disabled={createPackageLoading} maxLength={45} showCount />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Download Speed</span>}
            name="speedDown"
            rules={[
              {
                required: true,
                message: 'Please enter download speed',
              },
              {
                type: 'number',
                min: 1,
                message: 'Download speed must be greater than 0',
              },
            ]}
          >
            <InputNumber
              placeholder="Enter download speed"
              disabled={createPackageLoading}
              style={{ width: '100%' }}
              min={1}
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Upload Speed</span>}
            name="speedUp"
            rules={[
              {
                required: true,
                message: 'Please enter upload speed',
              },
              {
                type: 'number',
                min: 1,
                message: 'Upload speed must be greater than 0',
              },
            ]}
          >
            <InputNumber
              placeholder="Enter upload speed"
              disabled={createPackageLoading}
              style={{ width: '100%' }}
              min={1}
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Speed Unit</span>}
            name="speedUnit"
            rules={[
              {
                required: true,
                message: 'Please select speed unit',
              },
            ]}
          >
            <Select placeholder="Select speed unit" disabled={createPackageLoading}>
              <Option value="K">K (Kilobits)</Option>
              <Option value="M">M (Megabits)</Option>
              {/* <Option value="G">G (Gigabits)</Option> */}
            </Select>
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Duration Type</span>}
            name="packageDurationEnum"
            rules={[
              {
                required: true,
                message: 'Please select duration type',
              },
            ]}
          >
            <Select
              placeholder="Select duration type"
              disabled={createPackageLoading}
              onChange={(value) => {
                setPackageDurationEnum(value);
                form.setFieldsValue({
                  packageDurationDays: undefined,
                  packageDurationHours: undefined,
                });
              }}
            >
              <Option value="HOURS">Hours</Option>
              <Option value="DAYS">Days</Option>
            </Select>
          </Form.Item>

          {packageDurationEnum === 'HOURS' && (
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Number of Hours</span>}
              name="packageDurationHours"
              rules={[
                {
                  required: true,
                  message: 'Please enter number of hours',
                },
                {
                  type: 'number',
                  min: 1,
                  message: 'Hours must be greater than 0',
                },
              ]}
            >
              <InputNumber
                placeholder="Enter number of hours"
                disabled={createPackageLoading}
                style={{ width: '100%' }}
                min={1}
              />
            </Form.Item>
          )}

          {packageDurationEnum === 'DAYS' && (
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Number of Days</span>}
              name="packageDurationDays"
              rules={[
                {
                  required: true,
                  message: 'Please enter number of days',
                },
                {
                  type: 'number',
                  min: 1,
                  message: 'Days must be greater than 0',
                },
              ]}
            >
              <InputNumber
                placeholder="Enter number of days"
                disabled={createPackageLoading}
                style={{ width: '100%' }}
                min={1}
              />
            </Form.Item>
          )}

          <Form.Item name="hasDataLimit" valuePropName="checked">
            <Checkbox onChange={handleDataLimitCheckboxChange} disabled={createPackageLoading}>
              Data Limit
            </Checkbox>
          </Form.Item>

          {maxDataVolumeProvided && (
            <>
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Max Data Volume Limit</span>}
                name="maxDataVolumeLimit"
                rules={[
                  {
                    required: true,
                    message: 'Please enter max data volume limit',
                  },
                  {
                    type: 'number',
                    min: 1,
                    message: 'Max data volume limit must be greater than 0',
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter max data volume limit"
                  disabled={createPackageLoading}
                  style={{ width: '100%' }}
                  min={1}
                />
              </Form.Item>

              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Max Data Volume Unit</span>}
                name="maxDataVolumeUnit"
                rules={[
                  {
                    required: true,
                    message: 'Please select max data volume unit',
                  },
                ]}
              >
                <Select placeholder="Select max data volume unit" disabled={createPackageLoading}>
                  <Option value="K">K (Kilobytes)</Option>
                  <Option value="M">M (Megabytes)</Option>
                  <Option value="G">G (Gigabytes)</Option>
                </Select>
              </Form.Item>
            </>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleCancel} style={{ marginRight: '8px' }} disabled={createPackageLoading}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={createPackageLoading} disabled={createPackageLoading}>
              {createPackageLoading ? 'Creating...' : 'Create Package'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {apiLoading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <Typography style={{ marginTop: '16px', display: 'block' }}>Loading packages...</Typography>
        </div>
      ) : apiError && !apiLoading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty
            description={<Typography.Text type="danger">{apiError || 'Failed to load packages'}</Typography.Text>}
          />
        </div>
      ) : allPackages.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty description="No packages found. Create your first package!" />
        </div>
      ) : (
        <div className="table-container-custom">
          <Table
            className="antd-table-striped packages-table-clickable"
            columns={columns}
            dataSource={allPackages}
            pagination={false}
            loading={apiLoading}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' },
            })}
          />
        </div>
      )}
    </>
  );
};
export default PackagesComponent;
